import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { LocalizedNameMap } from 'pi-kiosk-shared';
import {
  clearPromoActivatedRewardHandoff,
  promoActivatedRewardStorageKey,
} from 'pi-kiosk-shared/promo/promoSessionHandoff';
import { ApiError } from '../../shared/api/ApiClient.js';
import { resolvePublicApiFailureMessage } from '../../shared/errors/publicApiFailureMessage.js';
import { useApiClient } from '../../shared/providers/AppDataProvider.js';
import { useTenantCode } from '../../shared/tenant/TenantProvider.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { customerQueryKeys } from '../../shared/api/queryKeys.js';
import { rewardsLog } from './logging.js';
import {
  orgDirectoryListRefetchInterval,
  orgDirectoryStaleTime,
} from '../tenant-select/hooks/useOrgDirectoryRealtimeSync.js';

/** G12 — promo tenant queries follow directory SSE stale/poll + tab-return parity. */
export type PromoTenantRealtimeOptions = {
  readonly directorySseConnected: boolean;
};

export function promoTenantQueryOptions(
  sseConnected: boolean,
): {
  staleTime: number;
  refetchInterval: number | false;
  refetchOnWindowFocus: true;
} {
  return {
    staleTime: orgDirectoryStaleTime(sseConnected),
    refetchInterval: orgDirectoryListRefetchInterval(sseConnected),
    refetchOnWindowFocus: true,
  };
}

function promoQueryExtras(
  realtime: PromoTenantRealtimeOptions | undefined,
): ReturnType<typeof promoTenantQueryOptions> | { staleTime: number } {
  return realtime !== undefined
    ? promoTenantQueryOptions(realtime.directorySseConnected)
    : { staleTime: 30_000 };
}

export type PromoRewardStatus = 'ISSUED' | 'ACTIVATED' | 'REDEEMED' | 'EXPIRED';

export interface PromoRewardDTO {
  readonly id: string;
  readonly eventId: string;
  readonly status: PromoRewardStatus;
  readonly discountAmount: number | null;
  readonly discountBps: number | null;
  readonly expiresAt: string | null;
  readonly issuedAt: string;
  readonly activatedAt: string | null;
}

export interface PromoEventDTO {
  readonly id: string;
  readonly name: string;
  /** Per-locale display-name overrides; null/omit = use `name`. */
  readonly nameLocales?: LocalizedNameMap | null;
  readonly status: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly enrollmentRequired: boolean;
  readonly applicationMode: string;
  /** PLATFORM | TENANT — present on GET /promotions/events summaries. */
  readonly ownerType?: string;
}

export interface PromoLineAdjustmentDTO {
  readonly lineKey: string;
  readonly productId: number;
  readonly variantId: number | null;
  readonly discountAmount: number;
  readonly discountedGross: number;
  readonly eventId: string;
  readonly ruleVersionId: string;
}

export interface PromoProgressHintDTO {
  readonly eventId: string;
  readonly current: number;
  readonly threshold: number;
  readonly periodKey: string;
}

export interface PromoPreviewPricingResultDTO {
  readonly adjustments: readonly PromoLineAdjustmentDTO[];
  readonly ineligibleOffers: readonly {
    eventId: string;
    reasonCode: string;
    messageKey: string;
  }[];
  readonly progressHints: readonly PromoProgressHintDTO[];
  readonly rejectedCandidates: readonly { ruleId: string; reasonCode: string }[];
  readonly policyVersion: string;
  readonly stackingMode: 'EXCLUSIVE' | 'STACK_PROMO_THEN_LOYALTY';
  readonly cartDiscountAmount: number;
}

export function usePromoRewards(
  customerId: number | null,
  promotionsEnabled: boolean,
  realtime?: PromoTenantRealtimeOptions,
) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.promoRewards(tenantCode, customerId ?? 0),
    enabled:
      promotionsEnabled &&
      hasCustomerApiTenant(tenantCode) &&
      customerId != null &&
      customerId > 0,
    queryFn: async () => {
      const response = await api.request<{ rewards: PromoRewardDTO[] }>(
        `/promotions/rewards/${customerId}`,
      );
      return response.rewards;
    },
    ...promoQueryExtras(realtime),
  });
}

export function usePromoEvents(
  customerId: number | null,
  queryEnabled: boolean,
  realtime?: PromoTenantRealtimeOptions,
) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.promoEvents(tenantCode, customerId ?? 0),
    enabled:
      queryEnabled &&
      hasCustomerApiTenant(tenantCode) &&
      customerId != null &&
      customerId > 0,
    queryFn: async () => {
      const response = await api.request<{ events: PromoEventDTO[] }>('/promotions/events');
      return response.events;
    },
    ...promoQueryExtras(realtime),
  });
}

export function usePromoAccountProgressHints(
  customerId: number | null,
  promotionsEnabled: boolean,
  realtime?: PromoTenantRealtimeOptions,
) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  return useQuery({
    queryKey: customerQueryKeys.promoProgressHints(tenantCode, customerId ?? 0),
    enabled:
      promotionsEnabled &&
      hasCustomerApiTenant(tenantCode) &&
      customerId != null &&
      customerId > 0,
    queryFn: async () => {
      const response = await api.request<PromoPreviewPricingResultDTO>(
        '/promotions/preview-pricing',
        {
          method: 'POST',
          body: {
            customerId,
            channel: 'CUSTOMER_PWA',
            items: [],
            activatedPromoRewardId: null,
          },
        },
      );
      return response.progressHints;
    },
    ...promoQueryExtras(realtime),
  });
}

export function useActivatePromoReward(customerId: number | null) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      rewardId: string;
      checkoutSessionPublicId?: string | null;
    }) => {
      if (customerId == null) {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 401, code: 'UNAUTHORIZED' }),
          401,
          'UNAUTHORIZED',
        );
      }
      const response = await api.request<{
        reward: { rewardId: string; status: PromoRewardStatus; ruleVersionId: string };
      }>(`/promotions/rewards/${encodeURIComponent(input.rewardId)}/activate`, {
        method: 'POST',
        body: {
          checkoutSessionPublicId: input.checkoutSessionPublicId ?? null,
        },
      });
      const sessionId = input.checkoutSessionPublicId?.trim();
      if (sessionId && typeof sessionStorage !== 'undefined') {
        try {
          clearPromoActivatedRewardHandoff(tenantCode, sessionId);
          sessionStorage.setItem(
            promoActivatedRewardStorageKey(tenantCode, sessionId),
            input.rewardId,
          );
        } catch (err: unknown) {
          rewardsLog.warn('Promo reward handoff sessionStorage failed', {
            operation: 'activateHandoff',
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }
      return response;
    },
    onSuccess: async () => {
      if (customerId != null) {
        await queryClient.invalidateQueries({
          queryKey: customerQueryKeys.promoRewards(tenantCode, customerId),
        });
      }
    },
  });
}

export function useEnrollPromoEvent(customerId: number | null) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: { eventId: string; idempotencyKey?: string }) => {
      if (customerId == null) {
        throw new ApiError(
          resolvePublicApiFailureMessage({ status: 401, code: 'UNAUTHORIZED' }),
          401,
          'UNAUTHORIZED',
        );
      }
      return api.request<{ enrollment: { enrollmentId: string; state: string } }>(
        `/promotions/events/${encodeURIComponent(input.eventId)}/enroll`,
        {
          method: 'POST',
          body: {},
          idempotencyKey: input.idempotencyKey,
        },
      );
    },
    onSuccess: async () => {
      if (customerId != null) {
        await queryClient.invalidateQueries({
          queryKey: customerQueryKeys.promoEvents(tenantCode, customerId),
        });
      }
    },
  });
}

export interface PromoPreviewLineInput {
  readonly productId: number;
  readonly variantId: number | null;
  readonly quantity: number;
  readonly unitPrice: number;
  readonly lineKey: string;
}

export function usePromoPreviewPricing(
  input: {
    customerId: number | null;
    promotionsEnabled: boolean;
    items: readonly PromoPreviewLineInput[];
    activatedPromoRewardId?: string | null;
    checkoutSessionId?: string | null;
  },
) {
  const api = useApiClient();
  const tenantCode = useTenantCode();
  const activatedFromHandoff =
    input.checkoutSessionId != null
      ? readActivatedPromoRewardHandoffFromStorage(tenantCode, input.checkoutSessionId)
      : null;
  const activatedPromoRewardId = input.activatedPromoRewardId ?? activatedFromHandoff;

  return useQuery({
    queryKey: customerQueryKeys.promoPreviewPricing(tenantCode, {
      customerId: input.customerId ?? 0,
      sessionId: input.checkoutSessionId ?? '',
      items: input.items,
      activatedPromoRewardId,
    }),
    enabled:
      hasCustomerApiTenant(tenantCode) &&
      input.promotionsEnabled &&
      input.customerId != null &&
      input.customerId > 0 &&
      input.items.length > 0,
    queryFn: async (): Promise<PromoPreviewPricingResultDTO> => {
      if (!hasCustomerApiTenant(tenantCode)) {
        return {
          adjustments: [],
          ineligibleOffers: [],
          progressHints: [],
          rejectedCandidates: [],
          policyVersion: '',
          stackingMode: 'EXCLUSIVE',
          cartDiscountAmount: 0,
        };
      }
      return api.request<PromoPreviewPricingResultDTO>(
        '/promotions/preview-pricing',
        {
          method: 'POST',
          body: {
            customerId: input.customerId,
            channel: 'CUSTOMER_CHECKOUT',
            items: input.items,
            activatedPromoRewardId,
            ...(input.checkoutSessionId
              ? { checkoutSessionPublicId: input.checkoutSessionId }
              : {}),
          },
        },
      );
    },
  });
}

function readActivatedPromoRewardHandoffFromStorage(
  tenantCode: string,
  checkoutSessionId: string,
): string | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }
  try {
    const value = sessionStorage.getItem(
      promoActivatedRewardStorageKey(tenantCode, checkoutSessionId),
    );
    return value != null && value.length > 0 ? value : null;
  } catch (err: unknown) {
    rewardsLog.warn('Promo reward handoff read failed', {
      operation: 'readHandoff',
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
}
